import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';

import { setEnrollmentLatest } from 'enrollment/components/BottomNavigation/services';
import { PSBannerWrapper, PSModalWrapper } from 'enrollment/components/Shared';
import {
  getPageRouter,
  getLastViewedPage,
} from 'enrollment/utils/common/Utils';
import {
  GET_ENROLLMENT_CONTEXT_QUERY_KEY,
  NEW_HIRE_ENROLLMENT_TYPE,
  OPEN_ENROLLMENT_TYPE,
  useConfigurationQuery,
} from 'theSource/queries';
import { useAvailablePlanYearsQuery } from 'theSource/queries/useAvailablePlanYearsQuery';
import { formatDateString, INCOMPLETE_ENROLLMENT_STATUSES } from '../../utils';
import { ContextCardProps } from '../ContextCard';
import { PsButtonTuner } from 'stencilSetup';
import { AlertBox, Card } from 'components';
import { REMOVE_LIFE_EVENT_QUERY_KEY } from 'enrollment/queries/useLifeEventQuery/utils';
import { ResponseDto, ErrorResponseDto } from 'theSource/types';
import { useApiMutation } from 'theSource/utils';
import { CheckCircleStyled } from 'enrollment/components/Header/components/CartDrawer/CartDrawerStyled';
import { AddPlanYearCard } from '..';
import { ROUTES } from 'enrollment/app/routes';
import { ROUTES as SOURCE_ROUTES } from 'theSource/app/routes';
import {
  setEnrollmentContext,
  setEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { selectSubscriberDetails } from 'enrollment/features/Profile/services';
import { selectLumosEnabled, selectOldEnrollmentUrl } from 'theSource/features';

export const LifeEventCard = ({
  enrollmentContext,
  cardIndex,
  allContexts,
  planYearStart,
  planYearEnd,
}: ContextCardProps) => {
  const { t } = useTranslation('pages.home');
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const subscriberData = useSelector(selectSubscriberDetails);
  const profileAvatar = subscriberData?.subscriber_profile?.avatar;
  const profilePicture = subscriberData?.subscriber_profile?.profile_picture;
  const lumosEnabled = useSelector(selectLumosEnabled);
  const oldEnrollmentUrl = useSelector(selectOldEnrollmentUrl);

  const status = enrollmentContext.status;
  const textIndex = INCOMPLETE_ENROLLMENT_STATUSES.includes(status) ? 0 : 1;

  const nextStatus = enrollmentContext?.next_status;
  const nextPlanYearId = enrollmentContext?.next_plan_year_id;
  const nextPlanYearData = enrollmentContext.included_plan_years.find(
    (nextPlanYear) => nextPlanYear.id === nextPlanYearId
  );
  const lifeEventPlanYears =
    enrollmentContext?.subscriber_life_event?.org_plan_year_ids;
  const nextTextIndex = INCOMPLETE_ENROLLMENT_STATUSES.includes(
    nextStatus || ''
  )
    ? 0
    : 1;

  const continueEnrollment =
    enrollmentContext.status === 'started' ||
    (enrollmentContext.status === 'not_started' &&
      enrollmentContext.current_category);
  const notstartedEnrollment = enrollmentContext.status === 'not_started';
  const initSubLifeEventID = enrollmentContext.subscriber_life_event?.id;
  const isAutoApprove = Boolean(
    enrollmentContext.subscriber_life_event?.auto_approve
  );
  const lastViewedPage = getLastViewedPage({
    currentCategory: enrollmentContext.current_category,
    requiredTask: enrollmentContext.required_tasks[0] || 'confirm',
  });

  const headerText = [t('leChanges'), t('leCompleteHeader')];
  const bodyText = [t('continueEnrollment'), t('leComplete')];
  const isMobile = useMediaQuery('(max-width: 744px)');

  const { configuration } = useConfigurationQuery();
  const { availablePlanYears, isLoadingAvailablePlanYears } =
    useAvailablePlanYearsQuery();
  const orgPyId =
    availablePlanYears?.data?.plan_years &&
    availablePlanYears?.data?.plan_years[0]
      ? availablePlanYears.data.plan_years[0].org_plan_year_id
      : 0;

  const isCompleteAndDualPY =
    status === 'complete' &&
    Boolean(availablePlanYears && availablePlanYears.data.plan_years[0]);

  let currentLifeEventplanYearEndsOn = planYearEnd;
  let currentLifeEventplanYearStartsOn = planYearStart;
  const numPlanYears =
    enrollmentContext.subscriber_life_event?.org_plan_year_ids.length;

  if (numPlanYears && numPlanYears > 1) {
    // handles multiple plan years on life events
    const lifeEventPlanYearIds =
      enrollmentContext.subscriber_life_event?.org_plan_year_ids.filter(
        (id) => id !== nextPlanYearId
      );
    const filteredPlanYears = enrollmentContext.included_plan_years.filter(
      (opy) => lifeEventPlanYearIds!.includes(opy.id)
    );
    const endsOnDates = filteredPlanYears.map((py) => py.ends_on);
    currentLifeEventplanYearEndsOn = formatDateString(
      endsOnDates
        .reduce((date0, date1) => (date0 > date1 ? date0 : date1))
        .toString()
    );

    const startsOnDates = filteredPlanYears.map((py) => py.starts_on);
    currentLifeEventplanYearStartsOn = formatDateString(
      startsOnDates
        .reduce((date0, date1) => (date0 > date1 ? date0 : date1))
        .toString()
    );
  }

  const { mutate: removeLE } = useApiMutation<
    ResponseDto,
    ErrorResponseDto,
    {}
  >({
    queryKey: REMOVE_LIFE_EVENT_QUERY_KEY,
    queryKeysToInvalidate: GET_ENROLLMENT_CONTEXT_QUERY_KEY,
    onSuccess: () => {
      dispatch(setEnrollmentContext(''));
    },
    method: 'post',
    endpoint: `${configuration?.apiHost}/v2/self_service/life_events/reject?subscriber_life_event_id=${initSubLifeEventID}`,
    getErrorMessage: (error) => error.response?.data?.details,
    showSuccessSnackbar: false,
  });

  const navToEnrollment = (enrollmentPlanYear: number | undefined) => {
    if (lumosEnabled && enrollmentPlanYear) {
      dispatch(
        setEnrollmentContext(enrollmentContext.enrollment_type_lookup_code)
      );
      if (
        nextPlanYearId &&
        lifeEventPlanYears &&
        lifeEventPlanYears.length > 1
      ) {
        dispatch(setEnrollmentOrgPlanYearId(enrollmentPlanYear));
      }
      dispatch(setEnrollmentLatest(lastViewedPage));
      if (notstartedEnrollment) {
        navigate(
          `${SOURCE_ROUTES.enrollment.path}/${ROUTES.enrollment.beforeStarted.path}`
        );
      } else if (continueEnrollment || !notstartedEnrollment) {
        if (Boolean(profilePicture) || Boolean(profileAvatar)) {
          navigate(
            `${SOURCE_ROUTES.enrollment.path}/${getPageRouter(lastViewedPage)}`
          );
        } else {
          navigate(
            `${SOURCE_ROUTES.enrollment.path}/${ROUTES.enrollment.profileEdit.path}`
          );
        }
      }
    } else if (oldEnrollmentUrl) {
      window.location.href = oldEnrollmentUrl;
    }
  };

  const renderHeader = (
    statusDetail: string,
    textIndexDetail: 0 | 1,
    planYearStart: string | undefined,
    planYearEnd: string | undefined
  ) => {
    return (
      <div>
        <div className="d-flex align-items-center">
          {status === 'complete' && !nextStatus && (
            <CheckCircleStyled size={40} className="mr-2" />
          )}
          <h2 className="mb-0">{headerText[textIndexDetail]}</h2>
        </div>
        <div className="body1 semiBold">{t('lePlanNext')}</div>
        <div className="body1">
          {t('lePlanYear')}
          {planYearStart?.toString()}-{planYearEnd?.toString()}
        </div>
        {statusDetail === 'complete' && (
          <div className="pt-2">
            <AlertBox innerText={t('lePendingApproval')} />
          </div>
        )}
      </div>
    );
  };

  const renderBody = (textIndexDetail: 0 | 1) => {
    return (
      <div className="pt-3">
        <div className="body1">{bodyText[textIndexDetail]}</div>
      </div>
    );
  };

  const getPendingText = (context: string | undefined) => {
    switch (context) {
      case NEW_HIRE_ENROLLMENT_TYPE:
        return t('newHire');
      case OPEN_ENROLLMENT_TYPE:
        return t('openEnrollmentHeader');
      default:
        return '';
    }
  };

  const renderButton = (
    statusDetail: string,
    enrollmentPlanYear: number | undefined,
    dualPlanYearLifeEvent = false
  ) => {
    const firstContext = allContexts && allContexts[0];
    const firstContextStatus = dualPlanYearLifeEvent
      ? firstContext?.next_status === 'complete'
      : firstContext?.status === 'complete';
    if (cardIndex > 0 && !firstContextStatus) {
      return (
        <div className="w-50">
          <PSBannerWrapper
            message={`${t('pending')} ${getPendingText(firstContext?.enrollment_type_lookup_code)}`}
            level="info"
            icon={true}
          />
        </div>
      );
    } else {
      return statusDetail === 'complete' ? (
        <PsButtonTuner
          buttonId={`update-my-benefits_${cardIndex}`}
          label={t('updateBenefits')}
          level="secondary"
          onClick={() => navToEnrollment(enrollmentPlanYear)}
        />
      ) : (
        <PsButtonTuner
          buttonId={`get_started_${cardIndex}`}
          label={t('continueEnrollmentBtn')}
          level="primary"
          className={`${isMobile ? '' : 'w-50'}`}
          onClick={() => navToEnrollment(enrollmentPlanYear)}
        />
      );
    }
  };

  const renderFooter = () => {
    return (
      <div
        className={`pt-3 d-flex w-100 ${isMobile ? 'flex-column' : 'flex-row'}`}
      >
        {renderButton(status, enrollmentContext.org_plan_year_id, false)}
        {initSubLifeEventID &&
          status !== 'complete' &&
          !isAutoApprove &&
          lumosEnabled && (
            <>
              <div
                className={`d-flex ${isMobile ? 'pt-3' : 'justify-content-end w-50'}`}
              >
                <ps-button
                  data-testid="discard-life-event"
                  id="discard-life-event"
                  level="secondary"
                  extraClass={`${isMobile ? '' : 'ml-3'}`}
                  other-data={`${`data-bs-toggle='modal' data-bs-target='#discardLE'`}`}
                  label={t('common:discardLE')}
                />
              </div>
              <PSModalWrapper
                id="discardLE"
                message={t('common:discardLEModalMessage')}
                title={t('common:discardLE')}
                primaryButton={t('common:discard')}
                primaryActive={true}
                secondaryButton={t('common:cancel')}
                extraClass="text-left"
                onModalDismiss={removeLE}
              ></PSModalWrapper>
            </>
          )}
      </div>
    );
  };

  const renderNextLifeEventFooter = (nextStatus: string) => {
    return (
      <div
        className={`pt-3 d-flex w-100 ${isMobile ? 'flex-column' : 'flex-row'}`}
      >
        {renderButton(nextStatus, enrollmentContext.next_plan_year_id, true)}
      </div>
    );
  };

  if (!enrollmentContext.subscriber_life_event || isLoadingAvailablePlanYears) {
    return null;
  }

  return (
    <>
      <Card
        showBorder
        additionalClasses="context"
        dataTestId="life-event-context-card"
      >
        <div className="border-bottom pb-3">
          {renderHeader(
            status,
            textIndex,
            currentLifeEventplanYearStartsOn,
            currentLifeEventplanYearEndsOn
          )}
        </div>
        <div>{renderBody(textIndex)}</div>
        <div>{renderFooter()}</div>
      </Card>
      {isCompleteAndDualPY && allContexts?.length === 1 && (
        <AddPlanYearCard
          orgPlanYearId={orgPyId}
          subscriberLifeEventId={
            availablePlanYears.data.subscriber_life_event_id
          }
          lifeEventCompleted={status === 'complete'}
        />
      )}
      {lifeEventPlanYears && lifeEventPlanYears.length > 1 && nextStatus && (
        <Card
          showBorder
          additionalClasses="context"
          dataTestId="life-event-context-card"
        >
          <div className="border-bottom pb-3">
            {renderHeader(
              nextStatus,
              nextTextIndex,
              formatDateString(nextPlanYearData?.starts_on.toString()),
              formatDateString(nextPlanYearData?.ends_on.toString())
            )}
          </div>
          <div>{renderBody(nextTextIndex)}</div>
          <div>{renderNextLifeEventFooter(nextStatus)}</div>
        </Card>
      )}
    </>
  );
};
